/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { type FunctionComponent, type JSX } from "react";
import { Outlet } from "react-router-dom";

import { NavigationProvider } from "./common";
import { AlertOverlayContainer } from "./common/AlertOverlay";
import { AppVersionOverlayContainer } from "./common/AppVersionOverlay";
import { FeatureOverrideContainer } from "./common/FeatureOverrideScreen";
import ConnectionRetryOverlayBridge from "./common/ConnectionRetryOverlayBridge";
import { MobileLandingDialog } from "./common/MobileLandingDialog";
import { UnityDialogContainer } from "./common/UnityDialog";
import { ToastMessageContainer } from "./common/ToastMessage";
import { MyHubRecordingHighlightContainer } from "./MyHub/MyHubRecordingHighlight";
import { SeriesRecordingOptionsOverlayContainer } from "./MyHub/SeriesRecordingOptionsOverlay";
import { OsdContainer } from "./common/Osd";
import { AppInitializer } from "./App/index";
import NavigationInterceptor from "./App/NavigationInterceptor";

/**
 * App shell — rendered as the root route element by createBrowserRouter.
 *
 * Provides:
 *   - NavigationProvider (D-pad engine context — must be inside Router)
 *   - AppInitializer (startup sequencing: endpoints → branding → signIn)
 *   - NavigationInterceptor (centralized side-effects on every navigation,
 *     including screenController sync for D-pad engine)
 *   - <Outlet /> renders matched child routes
 */
export const App: FunctionComponent = (): JSX.Element => {
  return (
    <NavigationProvider>
      <AppInitializer>
        <NavigationInterceptor />
        <MobileLandingDialog />
        <AlertOverlayContainer />
        <ConnectionRetryOverlayBridge />
        <UnityDialogContainer />
        <AppVersionOverlayContainer />
        <FeatureOverrideContainer />
        <ToastMessageContainer />
        <OsdContainer />
        <SeriesRecordingOptionsOverlayContainer />
        <MyHubRecordingHighlightContainer />
        <Outlet />
      </AppInitializer>
    </NavigationProvider>
  );
};
